import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { SEOPageLayout, InlineCTA } from "../../components/seo/SEOPageLayout";

const AREAS = [
  "Jacksonville", "Jacksonville Beach", "Atlantic Beach", "Neptune Beach", "Ponte Vedra Beach",
  "Orange Park", "Middleburg", "Green Cove Springs", "Fleming Island",
  "St. Augustine", "Fernandina Beach", "Amelia Island", "Yulee",
  "Mandarin", "San Marco", "Riverside", "Avondale", "Arlington", "Southside", "Westside",
];

export default function JacksonvilleService() {
  return (
    <SEOPageLayout
      title="Sell Diabetic Supplies for Cash in Jacksonville, FL"
      description="Sell eligible unused diabetic supplies in Jacksonville, FL. Local buyer, in-person meetups, and clear eligibility requirements. Contact us today."
      canonical="https://cashforteststripsjacksonville.com/sell-diabetic-supplies-jacksonville-fl/"
      breadcrumbLabel="Sell Diabetic Supplies in Jacksonville"
      primaryKeyword="Sell Diabetic Supplies Jacksonville"
      h1="Sell Eligible Diabetic Supplies for Cash in Jacksonville"
      intro="Cash for Test Strips Jacksonville is a locally operated buyer of eligible unused diabetic supplies. If you live in Jacksonville or a nearby city and have unopened supplies you no longer need, this page explains how to get started with a local seller-first process."
    >
      {({ openForm }) => (
        <>
          <section data-testid="section-jax">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>Sell Diabetic Supplies in Jacksonville</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed mb-4">
              We work directly with sellers throughout the Jacksonville area to review eligible unused diabetic supplies and complete transactions in person. There is no shipping, no mail-in wait time, and no third-party middleman. Every meetup happens at a public location that works for you.
            </p>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              If you would like to see what specific brands we accept before contacting us, review the <Link to="/accepted-diabetic-supplies/" className="text-[#0F5132] font-semibold underline">accepted diabetic supplies list</Link>.
            </p>
          </section>

          <section data-testid="section-process">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>How the Process Works</h2>
            <ol className="space-y-3 text-lg text-[#4B5563] list-decimal list-inside">
              <li>Text a photo of the front and back of the box to (904) 955-9646, or use our <Link to="/sell-diabetic-test-strips/" className="text-[#0F5132] font-semibold underline">Get Started form</Link>.</li>
              <li>We review the brand, product code, expiration date, and packaging condition.</li>
              <li>If eligible, we schedule a public, in-person meetup at a Jacksonville-area location convenient for you.</li>
            </ol>
          </section>

          <section data-testid="section-supplies-accepted">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>What Supplies Do We Accept?</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              We currently accept eligible items from Dexcom, FreeStyle Libre, Omnipod, Medtronic, Tandem, plus test strips from Accu-Chek, OneTouch, Contour Next, ReliOn, True Metrix, and FreeStyle. Insulin and injection supplies are reviewed case-by-case. See the <Link to="/accepted-diabetic-supplies/" className="text-[#0F5132] font-semibold underline">full accepted list</Link> for details.
            </p>
          </section>

          <section data-testid="section-eligibility">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>Eligibility Requirements</h2>
            <ul className="space-y-2 text-lg text-[#4B5563] list-disc list-inside">
              <li>Unopened, factory-sealed retail boxes.</li>
              <li>At least six months until the printed expiration date.</li>
              <li>Packaging in good condition with a visible barcode.</li>
              <li>Not associated with Medicaid or Medicare.</li>
            </ul>
          </section>

          <section data-testid="section-areas">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>Serving Jacksonville and Nearby Areas</h2>
            <div className="flex flex-wrap gap-2">
              {AREAS.map((a) => (
                <span key={a} className="inline-flex items-center gap-1.5 bg-[#F5F5F0] text-[#1F2937] px-3 py-1.5 rounded-full text-sm font-medium">
                  <MapPin className="w-3.5 h-3.5 text-[#EA580C]" />
                  {a}
                </span>
              ))}
            </div>
          </section>

          <section data-testid="section-contact">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>Contact Cash for Test Strips Jacksonville</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              Call or text <a href="tel:+19049559646" className="text-[#0F5132] font-semibold underline">(904) 955-9646</a>, or submit your supplies through the Get Started form. We respond quickly and let you know honestly whether your supplies qualify.
            </p>
          </section>

          <InlineCTA
            onGetStarted={openForm}
            headline="Ready to Get Started in Jacksonville?"
            sub="Send us a photo to find out if your supplies qualify."
          />
        </>
      )}
    </SEOPageLayout>
  );
}
